import RefreshIcon from '@mui/icons-material/Refresh';
import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import type { GridEventListener } from '@mui/x-data-grid-premium';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { type MouseEvent, useCallback, useContext, useEffect, useMemo } from 'react';

import type { PreviewList } from '~/analytics/events';
import { UserContext } from '~/auth/userContext';
import { useTableRowPreview } from '~/components/rowPreview/useTableRowPreview';
import type { LiftAIToolbarProps } from '~/components/table';
import { LiftAITable } from '~/components/table';
import { useTasksEnabled } from '~/components/tasks/useTasksEnabled';
import type { ActionItem } from '~/data/schemas/actionItem';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';

import { previewTarget, WorkQueuePreviewPanel } from './WorkQueuePreviewPanel';
import { getWorkQueueColumns } from './workQueueTableColumns';

type Props = {
  actionItems: ActionItem[];
  isLoading?: boolean;
  /** True while a revalidation is in flight — the toolbar's refresh spinner. */
  isValidating?: boolean;
  onRefresh: () => void;
  onOpenItem: (item: ActionItem) => void;
  onSnooze: (item: ActionItem, event: MouseEvent<HTMLElement>) => void;
  /** Bounds the grid to this height so it scrolls internally instead of growing the page. */
  height?: number | string;
  /** Names the list in analytics; anything but the page itself is a table inside a detail page. */
  previewList?: PreviewList;
  /** Off where a caller doesn't want rows previewed, such as a detail page's Needs Attention tab. */
  enablePreview?: boolean;
};

// Worst first, then oldest first inside a level: the thing that has been
// critical longest is the thing to open.
const initialSortModel = [
  { field: 'severity' as const, sort: 'desc' as const },
  { field: 'createdAt' as const, sort: 'asc' as const },
];

/**
 * The Work Queue grid. Everything is client-side — the page holds the whole queue rather
 * than a page of it, so the band above can total what the queue actually contains.
 */
export function WorkQueueTable({
  actionItems,
  isLoading = false,
  isValidating = false,
  onRefresh,
  onOpenItem,
  onSnooze,
  height,
  previewList = 'needs_attention',
  enablePreview = true,
}: Props) {
  const apiRef = useGridApiRef();
  const itemsById = useMemo(
    () => new Map(actionItems.map((item) => [item.id, item])),
    [actionItems],
  );
  const getItem = useCallback((rowId: string) => itemsById.get(rowId), [itemsById]);
  const canPreview = useCallback(
    (rowId: string) => previewTarget(getItem(rowId)) !== null,
    [getItem],
  );
  const preview = useTableRowPreview({
    list: previewList,
    apiRef,
    scope: previewList === 'needs_attention' ? 'page' : 'table',
    canPreview,
    enabled: enablePreview,
  });
  // A snoozed or resolved item leaves the queue; its panel would otherwise stay open, empty.
  const { previewId, close: closePreview } = preview.rowPreview;
  useEffect(() => {
    if (previewId && !itemsById.has(previewId)) closePreview();
  }, [previewId, itemsById, closePreview]);
  const { currencyCode } = useContext(UserContext);
  const terms = useLocaleTerms();
  const tasksEnabled = useTasksEnabled();

  const columns = useMemo(
    () =>
      getWorkQueueColumns({ currencyCode, terms, onSnooze, onReview: onOpenItem, tasksEnabled }),
    [currencyCode, terms, onSnooze, onOpenItem, tasksEnabled],
  );

  const onRowClick = useCallback<GridEventListener<'rowClick'>>(
    ({ row }) => onOpenItem(row as ActionItem),
    [onOpenItem],
  );

  const RefreshAction = useCallback(
    () => (
      <Tooltip title="Refresh">
        <span>
          <IconButton
            size="small"
            onClick={onRefresh}
            disabled={isValidating}
            aria-label="Refresh the list"
          >
            {isValidating ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
    ),
    [isValidating, onRefresh],
  );

  const toolbarProps = useMemo<LiftAIToolbarProps>(
    // No density control: rows here are `'auto'` height, sized to a title, a description
    // and sometimes a chip, so the setting has nothing to act on.
    () => ({
      CustomToolbarActions: RefreshAction,
      hideDensity: true,
      filterControls: preview.peekSwitch,
    }),
    [RefreshAction, preview.peekSwitch],
  );

  const initialState = useMemo(
    () => ({
      sorting: { sortModel: initialSortModel },
      pinnedColumns: { right: ['actions'] },
    }),
    [],
  );

  return (
    <>
      <LiftAITable<ActionItem>
        apiRef={apiRef}
        rows={actionItems}
        columns={columns}
        getRowId={(row) => row.id}
        loading={isLoading}
        tableHeaderTitle="Needs Attention"
        // The grid owns the search, since it owns every row — no page-level state
        // to thread through, and it reaches the description text too.
        enableQuickFilter
        quickFilterPlaceHolder="Search the queue…"
        toolbarProps={toolbarProps}
        disableRowSelectionOnClick
        onRowClick={onRowClick}
        initialState={initialState}
        getRowHeight={() => 'auto'}
        pageSizeOptions={[25, 50, 100]}
        paginationModel={{ pageSize: 25, page: 0 }}
        height={height}
        {...preview.tableProps}
      />
      {preview.enabled ? (
        <WorkQueuePreviewPanel
          rowPreview={preview.rowPreview}
          getItem={getItem}
          onOpenItem={onOpenItem}
        />
      ) : null}
    </>
  );
}
